/** Pure value <-> URL string codecs. No Angular dependency. */
export interface UrlCodec<T> {
  to(value: T | null | undefined): string | null; // null = remove param
  from(raw: string): T | undefined;
}

const isEmpty = (v: unknown) => v === '' || v === null || v === undefined;
const pad = (n: number) => String(n).padStart(2, '0');

export const UrlCodecs = {
  string: <UrlCodec<string>>{
    to: v => (isEmpty(v) ? null : String(v)),
    from: s => s,
  },
  int: <UrlCodec<number>>{
    to: v => (isEmpty(v) || Number.isNaN(v) ? null : String(v)),
    from: s => {
      const n = Number.parseInt(s, 10);
      return Number.isNaN(n) ? undefined : n;
    },
  },
  bool: <UrlCodec<boolean>>{
    to: v => (isEmpty(v) ? null : v ? '1' : '0'),
    from: s => (s === '1' ? true : s === '0' ? false : undefined),
  },
  /** Date object <-> yyyy-MM-dd (local) */
  isoDate: <UrlCodec<Date>>{
    to: v =>
      v instanceof Date && !Number.isNaN(v.getTime())
        ? `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`
        : null,
    from: s => {
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? undefined : d;
    },
  },
  /** ABP Suite date filters are ISO strings — validate, pass through */
  isoDateString: <UrlCodec<string>>{
    to: v => (isEmpty(v) ? null : String(v)),
    from: s => (Number.isNaN(new Date(s).getTime()) ? undefined : s),
  },
  csv: <UrlCodec<string[]>>{
    to: v => (v?.length ? v.join(',') : null),
    from: s => s.split(',').filter(Boolean),
  },
  oneOf: <T extends string>(allowed: readonly T[]): UrlCodec<T> => ({
    to: v => (isEmpty(v) ? null : String(v)),
    from: s => (allowed.includes(s as T) ? (s as T) : undefined),
  }),
  manyOf: <T extends string>(allowed: readonly T[]): UrlCodec<T[]> => ({
    to: v => (v?.length ? v.join(',') : null),
    from: s => s.split(',').filter((x): x is T => allowed.includes(x as T)),
  }),
};
